import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useHistory } from '../../router/history'
import AppContext from '../../state/AppContext'
import { useAuth } from '../../auth/AuthContext'
import { matchesRoute, visibleNavigation } from '../navigation'
import { ChartIcon, ChevronRightIcon, CloseIcon, CogIcon, DocumentIcon, HomeIcon, PeopleIcon, PlusIcon } from './icons'
import KarteraLogo from './KarteraLogo'

const CLOSED_GROUPS_KEY = 'kt-sidebar-closed-groups'

const readClosedGroups = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(CLOSED_GROUPS_KEY))
        return Array.isArray(saved) ? saved : []
    } catch (error) {
        return []
    }
}

const groupStyles = {
    main: { tone: 'bg-tone-blue text-tone-blue-ink', Icon: HomeIcon },
    operacion: { tone: 'bg-tone-teal text-tone-teal-ink', Icon: DocumentIcon },
    catalogos: { tone: 'bg-tone-purple text-tone-purple-ink', Icon: PeopleIcon },
    reportes: { tone: 'bg-tone-orange text-tone-orange-ink', Icon: ChartIcon },
    administracion: { tone: 'bg-tone-pink text-tone-pink-ink', Icon: CogIcon },
}

const fallbackTones = [
    'bg-tone-blue text-tone-blue-ink',
    'bg-tone-teal text-tone-teal-ink',
    'bg-tone-purple text-tone-purple-ink',
    'bg-tone-orange text-tone-orange-ink',
    'bg-tone-pink text-tone-pink-ink',
]

const DESKTOP_QUERY = '(min-width: 1024px)'

const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches)
    useEffect(() => {
        const media = window.matchMedia(DESKTOP_QUERY)
        const onChange = (event) => setIsDesktop(event.matches)
        media.addEventListener('change', onChange)
        return () => media.removeEventListener('change', onChange)
    }, [])
    return isDesktop
}

const activeGroupId = (groups, pathname) => groups.find((group) => group.items.some((item) => matchesRoute(pathname, item.to)))?.id

const GroupIcon = ({ group, index }) => {
    const style = groupStyles[group.id]
    const tone = style?.tone || fallbackTones[index % fallbackTones.length]
    const Icon = style?.Icon
    const MuiIcon = group.icon
    return (
        <span className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] ${tone}`}>
            {Icon ? <Icon /> : <MuiIcon style={{ fontSize: 16 }} />}
        </span>
    )
}

const NavTree = ({ groups, pathname, onNavigate }) => {
    const history = useHistory()
    const [closedGroups, setClosedGroups] = useState(readClosedGroups)

    useEffect(() => {
        const active = activeGroupId(groups, pathname)
        if (active) setClosedGroups((current) => current.filter((id) => id !== active))
    }, [groups, pathname])

    const toggle = (id) => {
        setClosedGroups((current) => {
            const next = current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
            window.localStorage.setItem(CLOSED_GROUPS_KEY, JSON.stringify(next))
            return next
        })
    }

    const goToNew = (event, item) => {
        event.preventDefault()
        event.stopPropagation()
        if (!item.canCreate) return
        onNavigate?.()
        history.push(item.newTo)
    }

    return (
        <div className="flex flex-col gap-0.5">
            {groups.map((group, index) => {
                const open = !closedGroups.includes(group.id)
                const panelId = `kartera-nav-${group.id}`
                return (
                    <div key={group.id}>
                        <button
                            type="button"
                            aria-expanded={open}
                            aria-controls={panelId}
                            onClick={() => toggle(group.id)}
                            className="flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-[10px] border-0 bg-transparent px-2.5 text-left text-[15px] font-bold text-k-ink hover:bg-k-hover"
                        >
                            <GroupIcon group={group} index={index} />
                            <span className="flex-1">{group.label}</span>
                            <ChevronRightIcon className={`shrink-0 text-k-muted-2 transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />
                        </button>

                        {open && (
                            <div id={panelId} className="mb-2 ml-6 mt-0.5 flex flex-col gap-0.5 border-l-2 border-k-line-soft pl-3.5">
                                {group.items.map((item) => {
                                    const active = matchesRoute(pathname, item.to)
                                    return (
                                        <div key={item.to} className="relative flex min-h-[38px] items-center">
                                            <Link
                                                to={item.to}
                                                onClick={onNavigate}
                                                aria-current={active ? 'page' : undefined}
                                                className={`relative flex min-h-[38px] min-w-0 flex-1 items-center justify-between gap-2 rounded-[9px] px-2.5 text-sm ${item.newTo ? 'pr-9' : ''} ${active
                                                    ? 'bg-k-accent-soft font-bold text-k-accent-ink'
                                                    : 'text-k-ink-2 hover:bg-k-hover hover:text-k-ink'}`}
                                            >
                                                {active && <span aria-hidden="true" className="absolute -left-4 bottom-2 top-2 w-0.5 rounded-sm bg-brand-indigo" />}
                                                <span className="truncate">{item.label}</span>
                                                {item.badge != null && (
                                                    <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-[11px] font-extrabold ${item.badgeHot
                                                        ? 'bg-k-danger-soft text-k-danger-ink'
                                                        : 'bg-k-line-soft text-k-ink-2'}`}
                                                    >
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </Link>
                                            {item.newTo && (
                                                <button
                                                    type="button"
                                                    title={item.newTooltip || 'Nuevo'}
                                                    aria-label={item.newTooltip || 'Nuevo'}
                                                    disabled={!item.canCreate}
                                                    onClick={(event) => goToNew(event, item)}
                                                    className={`absolute right-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-transparent bg-transparent transition duration-150 ease-out ${item.canCreate
                                                        ? 'cursor-pointer text-k-muted hover:border-k-line-strong hover:bg-k-surface hover:text-k-accent-ink hover:shadow-sm active:scale-90'
                                                        : 'cursor-not-allowed text-k-muted-2 opacity-40'}`}
                                                >
                                                    <PlusIcon />
                                                </button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

const Copyright = () => (
    <p className="mt-auto pb-3.5 pt-[18px] text-center text-xs text-k-muted-2">© {new Date().getFullYear()} Kartera 365</p>
)

const KarteraAside = () => {
    const { user } = useAuth()
    const { appContext, setAppContext } = useContext(AppContext)
    const { pathname } = useLocation()
    const groups = useMemo(() => visibleNavigation(user), [user])
    const isDesktop = useIsDesktop()
    const mobileOpen = !isDesktop && Boolean(appContext.navbar?.open)

    useEffect(() => {
        if (isDesktop && appContext.navbar?.open) setAppContext({ ...appContext, navbar: { open: false } })
    }, [isDesktop, appContext, setAppContext])

    const closeMobile = () => {
        if (mobileOpen) setAppContext({ ...appContext, navbar: { open: false } })
    }

    useEffect(() => {
        if (!mobileOpen) return undefined
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setAppContext({ ...appContext, navbar: { open: false } })
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [mobileOpen, appContext, setAppContext])

    return (
        <>
            <nav aria-label="Principal" className="hidden w-[280px] shrink-0 flex-col overflow-y-auto border-r border-k-line bg-k-surface px-3 pt-3.5 text-k-ink-2 lg:flex">
                <NavTree groups={groups} pathname={pathname} />
                <Copyright />
            </nav>

            <div className={`fixed inset-0 z-50 lg:hidden ${mobileOpen ? '' : 'pointer-events-none'}`} aria-hidden={!mobileOpen} inert={!mobileOpen || undefined}>
                <div
                    className={`absolute inset-0 bg-[rgba(10,12,20,0.5)] transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0'}`}
                    onClick={closeMobile}
                />
                <nav
                    aria-label="Principal"
                    className={`absolute inset-y-0 left-0 flex w-[280px] max-w-[85vw] flex-col overflow-y-auto border-r border-k-line bg-k-surface px-3 text-k-ink-2 transition-transform duration-300 ease-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
                >
                    <div className="flex min-h-[68px] items-center justify-between gap-2 px-1">
                        <KarteraLogo onClick={closeMobile} />
                        <button
                            type="button"
                            aria-label="Cerrar menú"
                            onClick={closeMobile}
                            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl border-0 bg-transparent text-k-ink-2 hover:bg-k-hover"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <NavTree groups={groups} pathname={pathname} onNavigate={closeMobile} />
                    <Copyright />
                </nav>
            </div>
        </>
    )
}

export default KarteraAside
